import React, { FunctionComponent } from 'react';
import { Divider, Stack, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import AppleYaVk from '../../image/apple_yavk.jpg';
import AppleVost from '../../image/apple_vost.jpg';
import {
  AuthHelpRestoreLayout,
  InstructionImage,
  InstructionSetup,
  InstructionSteps,
  StageHeader,
} from './AuthHelpRestoreLayout';

interface Props {
  showSetup: boolean;
}

const stages = [
  'Подготовьте вход через Яндекс ID или ВК ID',
  'Восстановите подписку',
  'Войдите на втором устройстве',
];

export const AuthHelpAppleRestoreItemDiv: FunctionComponent<Props> = ({
  showSetup,
}) => (
  <AuthHelpRestoreLayout
    stages={stages}
    idPrefix="apple-restore-stage"
    intro="Первые два этапа выполните на устройстве, где уже есть подписка."
  >
    <Stack
      component="section"
      id="apple-restore-stage-1"
      spacing={2.5}
      sx={{ scrollMarginTop: 24 }}
    >
      <StageHeader
        totalStages={stages.length}
        stage={1}
        title={stages[0]}
        secondDevice={false}
      />
      <InstructionSteps
        steps={[
          {
            id: 'app-store',
            text: (
              <>
                Откройте <strong>App Store</strong>.
              </>
            ),
          },
          {
            id: 'apple-profile',
            text: 'Нажмите на своё имя в правом верхнем углу.',
          },
          {
            id: 'apple-account',
            text: 'В открывшемся окне снова нажмите на своё имя.',
          },
          {
            id: 'region',
            text: (
              <>
                В разделе <strong>«Страна/регион»</strong> выберите{' '}
                <strong>Россию</strong>.
              </>
            ),
          },
        ]}
      />
      <InstructionImage
        image={AppleYaVk}
        label="Увеличить схему настройки App Store"
        aspectRatio="1280 / 738"
      />
      <Stack
        direction="row"
        spacing={1}
        sx={{ p: 1.5, bgcolor: '#eaf2f5', borderLeft: '3px solid #4f8298' }}
      >
        <RestartAltIcon
          sx={{ fontSize: 20, color: '#3c6b7e', flexShrink: 0, mt: 0.25 }}
        />
        <Typography component="p" fontSize="13px" lineHeight={1.7}>
          После смены региона <strong>перезапустите приложение Учи Мед</strong>.
        </Typography>
      </Stack>
    </Stack>

    <Divider />

    <Stack
      component="section"
      id="apple-restore-stage-2"
      spacing={2.5}
      sx={{ scrollMarginTop: 24 }}
    >
      <StageHeader
        totalStages={stages.length}
        stage={2}
        title={stages[1]}
        secondDevice={false}
      />
      <InstructionSteps
        steps={[
          {
            id: 'open-app',
            text: (
              <>
                Откройте приложение <strong>Учи Мед</strong>.
              </>
            ),
          },
          {
            id: 'profile',
            text: 'Откройте свой профиль в правом верхнем углу.',
          },
          {
            id: 'sign-out',
            text: (
              <>
                Нажмите на три точки рядом с именем и выберите{' '}
                <strong>«Выйти из профиля»</strong>.
              </>
            ),
          },
          {
            id: 'apple-sign-in',
            important: true,
            text: (
              <>
                Нажмите именно <strong>«Войти с Apple»</strong>.
              </>
            ),
          },
          {
            id: 'choose-id',
            text: (
              <>
                В окне <strong>«Вход через Apple ID недоступен»</strong>{' '}
                выберите вход через <strong>Яндекс ID</strong> или{' '}
                <strong>ВК ID</strong>.
              </>
            ),
          },
          {
            id: 'restore',
            text: (
              <>
                После авторизации на экране{' '}
                <strong>«Привязать старый аккаунт»</strong> нажмите{' '}
                <strong>«Восстановить доступ»</strong>.
              </>
            ),
          },
          {
            id: 'check-subscriptions',
            text: (
              <>
                После входа нажмите <strong>«Продолжить»</strong> и проверьте
                вкладку <strong>«Мои подписки»</strong>.
              </>
            ),
          },
        ]}
      />
      <InstructionImage
        image={AppleVost}
        label="Увеличить схему восстановления подписки"
        aspectRatio="1280 / 440"
      />
    </Stack>

    <Divider />

    <Stack
      component="section"
      id="apple-restore-stage-3"
      spacing={2.5}
      sx={{ scrollMarginTop: 24 }}
    >
      <StageHeader
        totalStages={stages.length}
        stage={3}
        title={stages[2]}
        secondDevice
      />
      <Stack
        direction="row"
        spacing={1}
        sx={{ p: 1.5, bgcolor: '#eaf2f5', borderLeft: '3px solid #4f8298' }}
      >
        <InfoOutlinedIcon
          sx={{ fontSize: 20, color: '#3c6b7e', flexShrink: 0, mt: 0.25 }}
        />
        <Typography component="p" fontSize="13px" lineHeight={1.7}>
          Если вы уже входили на втором устройстве,{' '}
          <strong>выйдите из профиля и войдите заново</strong> после
          восстановления подписки.
        </Typography>
      </Stack>
      {showSetup && <InstructionSetup />}
      <InstructionSteps
        steps={[
          {
            id: 'second-device',
            text: (
              <>
                Откройте <strong>Учи Мед</strong> на втором устройстве.
              </>
            ),
          },
          {
            id: 'same-account',
            text: (
              <>
                Войдите через <strong>тот же Яндекс ID или ВК ID</strong>,
                который выбрали на этапе 2.
              </>
            ),
          },
          {
            id: 'second-subscriptions',
            text: (
              <>
                Проверьте вкладку <strong>«Мои подписки»</strong>.
              </>
            ),
          },
        ]}
      />
    </Stack>
  </AuthHelpRestoreLayout>
);
